import { useState, useRef, useEffect, useMemo } from 'react';
import { LayoutGrid, Sparkles, FileText, Plus, LogOut, Users, Target, Menu, X, CheckSquare, CalendarDays, Settings, Moon, ChevronRight, User, Inbox } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAccountLinks } from '../hooks/useAccountLinks';
import { useTasks, TASK_STATUSES } from '../hooks/useTasks';

export default function Header({ activeTab, setActiveTab, onAdd }) {
  const { user, userProfile, logout, isAdmin } = useAuth();
  const { pendingCount: pendingInvitationsCount } = useAccountLinks();
  const { getInboxTasks, addTask } = useTasks();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [quickAddValue, setQuickAddValue] = useState('');
  const tabRefs = useRef({});
  const settingsRef = useRef(null);
  const quickAddRef = useRef(null);

  // Get inbox count for badge
  const inboxCount = useMemo(() => getInboxTasks().length, [getInboxTasks]);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build tabs based on user profile settings
  const taskManagementEnabled = userProfile?.taskManagementEnabled ?? true;

  const tabs = useMemo(() => {
    const baseTabs = [];

    // Only show task management tabs if enabled
    if (taskManagementEnabled) {
      baseTabs.push(
        { id: 'cockpit', label: 'Dashboard', icon: LayoutGrid },
        { id: 'inbox', label: 'Inbox', icon: Inbox, badge: inboxCount > 0 ? inboxCount : null },
      );
    }

    baseTabs.push(
      { id: 'habits', label: 'Habits', icon: Sparkles },
    );

    if (taskManagementEnabled) {
      baseTabs.push(
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'schedule', label: 'Schedule', icon: CalendarDays },
        { id: 'eod', label: 'Daily Review', icon: Moon },
      );
    }

    baseTabs.push(
      { id: 'oneOnOne', label: '1:1s', icon: Users },
      { id: 'documents', label: 'Goals', icon: Target },
      { id: 'policies', label: 'Policies', icon: FileText },
    );

    return baseTabs;
  }, [taskManagementEnabled, inboxCount]);

  const initials = user?.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U';

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  const handleQuickAdd = async () => {
    if (!quickAddValue.trim()) return;
    await addTask({
      title: quickAddValue.trim(),
      status: TASK_STATUSES.INBOX
    });
    setQuickAddValue('');
  };

  return (
    <header className="glass-strong sticky top-0 z-[100] border-b border-border">
      <div className="max-w-[1400px] mx-auto px-3 md:px-6">
        <div className="flex items-center justify-between h-12 md:h-14">
          {/* Logo & Brand */}
          <div className="flex items-center gap-2 md:gap-3">
            <div className="relative group">
              <div className="absolute inset-0 bg-accent rounded-lg blur-md opacity-30 group-hover:opacity-50 transition-opacity" />
              <img src="/helm-icon.png" alt="Helm" className="relative w-8 h-8 rounded-lg shadow-md object-contain" />
            </div>
            <div>
              <h1 className="m-0 text-sm md:text-base font-semibold text-text-primary tracking-tight">
                Helm
              </h1>
              <p className="m-0 text-[10px] text-text-tertiary font-medium tracking-wide uppercase hidden md:block">
                Houston Branch
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center relative">
            <div className="flex bg-surface-hover rounded-lg p-1 border border-border">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  ref={(el) => (tabRefs.current[tab.id] = el)}
                  onClick={() => handleTabClick(tab.id)}
                  className={`relative px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer flex items-center gap-1.5 border-none transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-white text-text-primary shadow-sm'
                      : 'bg-transparent text-text-tertiary hover:text-text-primary hover:bg-white/50'
                  }`}
                >
                  <tab.icon size={14} className={activeTab === tab.id ? 'text-accent' : ''} />
                  {tab.label}
                  {tab.badge && (
                    <span className="ml-0.5 px-1.5 py-0.5 bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </nav>

          {/* Desktop Right Side */}
          <div className="hidden md:flex items-center gap-2">
            {/* Quick Add Input */}
            <div className="flex items-center gap-1.5 bg-surface-card border border-border rounded-lg px-2 py-1">
              <input
                ref={quickAddRef}
                type="text"
                value={quickAddValue}
                onChange={(e) => setQuickAddValue(e.target.value)}
                placeholder="Quick add to inbox..."
                className="w-40 lg:w-52 bg-transparent border-none outline-none text-xs text-text-primary placeholder:text-text-muted"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleQuickAdd();
                }}
              />
              <button
                onClick={handleQuickAdd}
                disabled={!quickAddValue.trim()}
                className="p-1 bg-accent text-white rounded cursor-pointer border-none hover:bg-accent/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={14} />
              </button>
            </div>

            {isAdmin && (activeTab === 'habits' || activeTab === 'policies' || activeTab === 'schedule' || activeTab === 'documents') && onAdd && (
              <button
                onClick={onAdd}
                className="group relative px-3 py-1.5 bg-accent hover:bg-orange-hover text-white rounded-lg text-xs font-semibold cursor-pointer flex items-center gap-1.5 border-none shadow-md shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              >
                <Plus size={14} strokeWidth={2.5} />
                <span>Add {activeTab === 'policies' ? 'Policy' : activeTab === 'schedule' ? 'Block' : activeTab === 'documents' ? 'Goal' : 'Habit'}</span>
              </button>
            )}

            <div className="flex items-center gap-1.5">
              {/* Settings Dropdown */}
              <div className="relative" ref={settingsRef}>
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className={`relative p-1.5 rounded-lg border border-transparent text-text-tertiary cursor-pointer flex items-center hover:bg-surface-hover hover:text-text-primary transition-all ${settingsOpen ? 'bg-surface-hover text-text-primary' : 'bg-transparent'}`}
                  title="Settings"
                  aria-label={pendingInvitationsCount > 0 ? `Settings, ${pendingInvitationsCount} pending invitation${pendingInvitationsCount > 1 ? 's' : ''}` : 'Settings'}
                  aria-expanded={settingsOpen}
                  aria-haspopup="true"
                >
                  <Settings size={16} aria-hidden="true" />
                  {pendingInvitationsCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent text-white text-[9px] font-bold rounded-full flex items-center justify-center" aria-hidden="true">
                      {pendingInvitationsCount}
                    </span>
                  )}
                </button>

                {settingsOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-surface-card border border-border rounded-lg shadow-lg z-50 min-w-[260px] overflow-hidden">
                    {/* Profile & Team Link */}
                    <button
                      onClick={() => {
                        setActiveTab('settings');
                        setSettingsOpen(false);
                      }}
                      className="w-full p-3 flex items-center justify-between gap-2 hover:bg-surface-hover cursor-pointer border-none bg-transparent text-left transition-all border-b border-border"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${pendingInvitationsCount > 0 ? 'bg-accent/10' : 'bg-surface-elevated'}`}>
                          <User size={16} className={pendingInvitationsCount > 0 ? 'text-accent' : 'text-text-tertiary'} />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-text-primary">Profile & Team</p>
                          <p className="text-[10px] text-text-tertiary">
                            {pendingInvitationsCount > 0 ? `${pendingInvitationsCount} pending invitation${pendingInvitationsCount > 1 ? 's' : ''}` : 'Manage your settings'}
                          </p>
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-text-tertiary" />
                    </button>

                  </div>
                )}
              </div>

              <div
                className="w-8 h-8 rounded-full bg-surface-card border border-border hover:border-accent/50 flex items-center justify-center text-text-secondary font-semibold text-xs cursor-default transition-all"
                title={user?.displayName || user?.email}
              >
                {initials}
              </div>
              <button
                onClick={logout}
                className="p-1.5 rounded-lg bg-transparent border border-transparent text-text-tertiary cursor-pointer flex items-center hover:bg-surface-hover hover:text-text-primary transition-all"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Mobile Right Side */}
          <div className="flex md:hidden items-center gap-1.5">
            {isAdmin && (activeTab === 'habits' || activeTab === 'policies' || activeTab === 'schedule' || activeTab === 'documents') && onAdd && (
              <button
                onClick={onAdd}
                className="p-1.5 bg-accent text-white rounded-lg cursor-pointer flex items-center border-none shadow-md shadow-accent/20"
                aria-label={`Add ${activeTab === 'policies' ? 'Policy' : activeTab === 'schedule' ? 'Block' : activeTab === 'documents' ? 'Goal' : 'Habit'}`}
              >
                <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
              </button>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 rounded-lg bg-surface-card border border-border text-text-primary cursor-pointer flex items-center hover:bg-surface-hover transition-all"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 glass-strong border-b border-border animate-fade">
          <div className="max-w-[1400px] mx-auto px-3 py-2">
            <nav className="flex flex-col gap-0.5">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`px-3 py-2.5 rounded-lg text-xs font-medium cursor-pointer flex items-center gap-2.5 border-none text-left transition-all animate-in opacity-0`}
                  style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
                >
                  <div className={`relative p-1.5 rounded-md ${activeTab === tab.id ? 'bg-accent-muted' : 'bg-surface-card'}`}>
                    <tab.icon size={14} className={activeTab === tab.id ? 'text-accent' : 'text-text-tertiary'} />
                    {tab.badge && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                        {tab.badge > 9 ? '9+' : tab.badge}
                      </span>
                    )}
                  </div>
                  <span className={activeTab === tab.id ? 'text-text-primary' : 'text-text-secondary'}>
                    {tab.label}
                  </span>
                  {activeTab === tab.id && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-accent" />
                  )}
                </button>
              ))}
            </nav>

            {/* Mobile user section */}
            <div className="border-t border-border mt-2 pt-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-surface-card border border-border flex items-center justify-center text-text-secondary font-semibold text-[10px]">
                  {initials}
                </div>
                <div>
                  <p className="text-xs text-text-primary font-medium">{user?.displayName}</p>
                  <p className="text-[10px] text-text-tertiary">{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleTabClick('settings')}
                  className="relative p-1.5 rounded-lg bg-surface-card border border-border text-text-tertiary cursor-pointer flex items-center hover:bg-surface-hover hover:text-text-primary transition-all"
                  title="Settings"
                  aria-label={pendingInvitationsCount > 0 ? `Settings, ${pendingInvitationsCount} pending invitation${pendingInvitationsCount > 1 ? 's' : ''}` : 'Settings'}
                >
                  <Settings size={16} aria-hidden="true" />
                  {pendingInvitationsCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-accent text-white text-[8px] font-bold rounded-full flex items-center justify-center" aria-hidden="true">
                      {pendingInvitationsCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={logout}
                  className="p-1.5 rounded-lg bg-surface-card border border-border text-text-tertiary cursor-pointer flex items-center hover:bg-surface-hover hover:text-text-primary transition-all"
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <LogOut size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
